export const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || process.env.PUBLIC_GA_MEASUREMENT_ID;

type GtagEventParams = {
  category?: string;
  label?: string;
  value?: number;
  [key: string]: any;
};

// Funnel-specific types
type FunnelType = 'acquisition' | 'tool_engagement' | 'monetization' | 'retention';
type FunnelStep = string;

interface SessionData {
  sessionId: string;
  clientId?: string;
  timestamp: number;
  source?: string;
  utm?: Record<string, string>;
  redirectType?: 'oauth' | 'stripe';
}

interface PurchaseData {
  transaction_id: string;
  value: number;
  currency?: string;
  items?: any[];
}

interface ToolUsageData {
  tool_id: string;
  action: string;
  credits_used?: number;
  processing_time?: number;
  success?: boolean;
}

// Session persistence utilities
export function generateSessionId(): string {
  return `pfp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export function getOrCreateSessionId(): string {
  if (typeof window === "undefined") return "";

  // 1. URL param — set by landing-page CTA decoration
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const fromUrl = urlParams.get("pfp_sid");
    if (fromUrl) {
      sessionStorage.setItem('pfp_session_id', fromUrl);
      localStorage.setItem('pfp_session_backup', fromUrl);
      window.history.replaceState({}, "", removeUrlParam(window.location.href, "pfp_sid"));
      return fromUrl;
    }
  } catch { /* non-critical */ }

  // 2. Existing session storage (with localStorage backup)
  let sessionId = sessionStorage.getItem('pfp_session_id');
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem('pfp_session_id', sessionId);
    // Also store in localStorage as backup
    localStorage.setItem('pfp_session_backup', sessionId);
  }
  return sessionId;
}

/** Strips a query param from a URL string without a page reload. */
function removeUrlParam(url: string, param: string): string {
  try {
    const u = new URL(url, window.location.origin);
    u.searchParams.delete(param);
    return u.href;
  } catch {
    return url;
  }
}

export function getClientId(): Promise<string | null> {
  return new Promise((resolve) => {
    if (!GA_MEASUREMENT_ID || typeof window === "undefined") {
      resolve(null);
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const gtag = (window as any).gtag as ((...args: any[]) => void) | undefined;
    if (!gtag) {
      resolve(null);
      return;
    }

    gtag('get', GA_MEASUREMENT_ID, 'client_id', (clientId: string) => {
      resolve(clientId);
    });
  });
}

// Targeted session restoration for OAuth/Stripe redirects only
export function storeSessionForRedirect(redirectType: 'oauth' | 'stripe'): void {
  if (typeof window === "undefined") return;

  const sessionData: SessionData = {
    sessionId: getOrCreateSessionId(),
    timestamp: Date.now(),
    source: window.location.pathname,
    utm: getUTMParams(),
    redirectType: redirectType
  };

  // Store in localStorage for persistence across redirects
  localStorage.setItem('pfp_redirect_session', JSON.stringify(sessionData));
}

export function restoreAndCleanupRedirectSession(): SessionData | null {
  if (typeof window === "undefined") return null;

  const stored = localStorage.getItem('pfp_redirect_session');
  if (stored) {
    try {
      const sessionData = JSON.parse(stored);
      // Immediately delete after reading
      localStorage.removeItem('pfp_redirect_session');
      return sessionData;
    } catch (e) {
      console.warn('Failed to parse redirect session data:', e);
      // Clean up corrupted data
      localStorage.removeItem('pfp_redirect_session');
    }
  }

  return null;
}

export function getUTMParams(): Record<string, string> {
  if (typeof window === "undefined") return {};

  const params = new URLSearchParams(window.location.search);
  const utmParams: Record<string, string> = {};

  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(param => {
    const value = params.get(param);
    if (value) {
      utmParams[param] = value;
    }
  });

  return utmParams;
}

// Enhanced tracking functions
export function pageview(url: string) {
  if (!GA_MEASUREMENT_ID) {
    console.warn('[GA] No measurement ID configured');
    return;
  }
  if (typeof window === "undefined") return;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const gtag = (window as any).gtag as ((...args: any[]) => void) | undefined;
  if (!gtag) {
    console.error('[GA] gtag not available - script may not have loaded. Check for ad blockers or CSP issues.');
    return;
  }

  const sessionId = getOrCreateSessionId();

  // Track pageview with session context
  gtag("event", "page_view", {
    page_path: url,
    custom_session_id: sessionId,
    ...getUTMParams()
  });

}

export function trackEvent(action: string, params: GtagEventParams = {}) {
  if (!GA_MEASUREMENT_ID) {
    console.warn('[GA] No measurement ID configured');
    return;
  }
  if (typeof window === "undefined") return;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const gtag = (window as any).gtag as ((...args: any[]) => void) | undefined;
  if (!gtag) {
    console.error('[GA] gtag not available - script may not have loaded. Check for ad blockers or CSP issues.');
    console.error('[GA] Failed to track event:', action, params);
    return;
  }

  // Always include session ID in events
  const sessionId = getOrCreateSessionId();
  gtag("event", action, {
    ...params,
    custom_session_id: sessionId
  });

}

export function trackFunnelEvent(funnelType: FunnelType, step: FunnelStep, params: GtagEventParams = {}) {
  const sessionId = getOrCreateSessionId();

  trackEvent(`${funnelType}_${step}`, {
    ...params,
    funnel_type: funnelType,
    funnel_step: step,
    custom_session_id: sessionId,
    timestamp: Date.now()
  });

  // Also track as a generic funnel progression event for easier analysis
  trackEvent('funnel_progression', {
    funnel_type: funnelType,
    step: step,
    custom_session_id: sessionId,
    ...params
  });
}

export function trackPurchase(data: PurchaseData & {
  purchase_trigger?: string;
  package_type?: string;
  credits?: number;
}) {
  // Build proper e-commerce items array
  const items = data.items && data.items.length > 0 ? data.items : [
    {
      item_id: data.transaction_id,
      item_name: data.credits ? `${data.credits} Credits` : (data.package_type || 'Purchase'),
      item_category: data.package_type || 'credits',
      quantity: 1,
      price: data.value
    }
  ];

  trackEvent('purchase', {
    transaction_id: data.transaction_id,
    value: data.value,
    currency: data.currency || 'USD',
    items: items,
    tax: 0,
    shipping: 0,
    affiliation: 'PFPMaker',
    custom_session_id: getOrCreateSessionId(),
    purchase_trigger: data.purchase_trigger,
    package_type: data.package_type,
    credits: data.credits
  });
}

export function trackToolUsage(data: ToolUsageData) {
  trackEvent('tool_usage', {
    tool_id: data.tool_id,
    action: data.action,
    credits_used: data.credits_used,
    processing_time: data.processing_time,
    success: data.success,
    custom_session_id: getOrCreateSessionId()
  });
}

export function setUserId(userId: string) {
  if (typeof window === "undefined") return;

  // Always persist user ID for own analytics — must not depend on GA loading
  sessionStorage.setItem('pfp_user_id', userId);

  if (!GA_MEASUREMENT_ID) return;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const gtag = (window as any).gtag as ((...args: any[]) => void) | undefined;
  if (!gtag) return;

  gtag("set", { user_id: userId });
}

export function getUserId(): string | null {
  if (typeof window === "undefined") return null;
  return sessionStorage.getItem('pfp_user_id');
}

// Store purchase context for tracking on return from Stripe
export function storePurchaseContext(data: {
  price_id: string;
  mode: string;
  credits?: number;
  purchase_trigger?: string;
  package_type?: string;
  [key: string]: any;
}): void {
  if (typeof window === "undefined") return;

  const context = {
    ...data,
    timestamp: Date.now(),
    session_id: getOrCreateSessionId()
  };

  sessionStorage.setItem('pfp_purchase_context', JSON.stringify(context));
}

// Retrieve and optionally clear purchase context
export function getPurchaseContext(clear: boolean = false): Record<string, any> | null {
  if (typeof window === "undefined") return null;

  const stored = sessionStorage.getItem('pfp_purchase_context');
  if (!stored) return null;

  try {
    const context = JSON.parse(stored);
    if (clear) {
      sessionStorage.removeItem('pfp_purchase_context');
    }
    return context;
  } catch (e) {
    console.warn('Failed to parse purchase context:', e);
    sessionStorage.removeItem('pfp_purchase_context');
    return null;
  }
}


